import { MouseEvent, useEffect, useState } from 'react'
import { useRef } from 'react'
import classnames from 'classnames/bind'
import { isEmpty } from 'lodash-es'

// components
import Image from '@/components/common/Image'
import Dots from './components/Dots'

// types
import { PlaceholderType } from '@/components/common/Image'
import { ITagItem } from '@/components/shared/ProductCard'

// Libs
import handleImageBlur from '@/methods/handleImageBlur'
import { hasDoubleEleven } from '@/methods/badge/hasDoubleEleven'
import useMedia from '@/hooks/useMedia'

// Styles
import styles from './style.module.scss'

interface Icarousell {
  images: string[]
  tags: ITagItem[]
  handleClick: (e?: React.MouseEvent<HTMLDivElement>) => void
}
const cx = classnames.bind(styles)

const Carousell = ({ images = [], tags = [], handleClick = () => {} }: Icarousell) => {
  const [activeIndex, setActiveIndex] = useState(0)

  const { isDesktop } = useMedia()

  const hasPagination = images.length > 1

  const swiperRef: any = useRef(null)
  const startXRef = useRef<number | null>(null)
  const startYRef = useRef<number | null>(null)

  const handleDeltaX = (deltaX: number) => {
    if (deltaX === 0) {
      handleClick()
      return
    }
    if (deltaX > 0) {
      const validIndex = handleValidIndex(activeIndex - 1)
      setActiveIndex(validIndex)
    } else {
      const validIndex = handleValidIndex(activeIndex + 1)
      setActiveIndex(validIndex)
    }
  }

  const handleMouseDown = (event: MouseEvent) => {
    event.stopPropagation()
    startXRef.current = event.clientX
    // FIXME: 暫時沒有辦法解決滑出卡牌邊界外，還是會紀錄 startX 的 BUG
    // 在A卡牌按下滑鼠 到任意其他張卡牌放開，再從任意地方按下滑鼠，到A卡牌放開，會造成A卡牌滑動
    setTimeout(() => {
      startXRef.current = null
    }, 600)
  }
  const handleMouseUp = (event: MouseEvent) => {
    const endX = event.clientX

    if (startXRef.current) {
      const deltaX = endX - startXRef.current
      handleDeltaX(deltaX)
      startXRef.current = null
    }
  }
  const handleTouchStart = (event: any) => {
    startXRef.current = event.touches[0].clientX
    startYRef.current = event.touches[0].clientY
  }
  const handleTouchEnd = (event: any) => {
    const endX = event.changedTouches[0].clientX
    const endY = event.changedTouches[0].clientY
    if (startXRef.current && startYRef.current) {
      const deltaX = endX - startXRef.current
      const deltaY = endY - startYRef.current

      // 如果水平位移大於垂直位移，判斷為左右滑
      if (Math.abs(deltaX) >= Math.abs(deltaY)) {
        handleDeltaX(deltaX)
        startXRef.current = null
      } else {
        return
      }
    }
  }
  const handleValidIndex = (index: number) => {
    if (index < 0) {
      return images.length - 1
    } else if (index >= images.length) {
      return 0
    }
    return index
  }

  useEffect(() => {
    swiperRef.current.style = `transform: translate3d(-${activeIndex * 100}%, 0px, 0px); transition-duration: 300ms;`
  }, [activeIndex])

  return (
    <div
      className={cx('carousell', 'no-swipe')}
      onClick={(event) => event.stopPropagation()}
      onTouchStart={!isDesktop ? handleTouchStart : undefined}
      onTouchEnd={!isDesktop ? handleTouchEnd : undefined}
      onMouseDown={isDesktop ? handleMouseDown : undefined}
      onMouseUp={isDesktop ? handleMouseUp : undefined}
      onDragStart={(event) => event.preventDefault()} // 阻止拖動
    >
      <div className={cx('carousell-wrapper')} ref={swiperRef}>
        {images.map((image, index) => (
          <div className={cx('carousell-static')} key={index}>
            {index === activeIndex ? (
              <Image
                src={image}
                alt='product image'
                fill
                sizes='(max-width: 500px) 160px,(max-width: 1000px) 320px,(max-width: 1440px) 24vw, 10vw'
                placeholder={PlaceholderType.blur}
                blurDataURL={handleImageBlur()}
                style={{ objectFit: 'contain', objectPosition: 'center' }}
              />
            ) : (
              <Image
                src={handleImageBlur()}
                alt='product image'
                fill
                sizes='(max-width: 710px) 60vw, (max-width: 1200px) 50vw, 33vw'
                placeholder={PlaceholderType.blur}
                blurDataURL={handleImageBlur()}
                style={{ objectFit: 'contain', objectPosition: 'center' }}
              />
            )}
          </div>
        ))}

        {!isEmpty(tags) && tags?.length > 0 && hasDoubleEleven(tags) && (
          <div className={cx('carousell__badge-double-11')}>
            <Image src='/images/badge-double-11.png' fill alt='' />
          </div>
        )}
      </div>
      {hasPagination && <Dots activeIndex={activeIndex} dataLength={images.length} />}
    </div>
  )
}
export default Carousell
